import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Divider,
  Typography,
  IconButton,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { ReplyOutlined } from '@material-ui/icons';
import { useParams, Link } from 'react-router-dom';
import useStyles from './styles';
import CreateTaskModal from '../../CreateTaskModal';
import TaskDetailComponent from '../../DetailComponents/TaskDetailComponent/index';
import AssignedUsers from '../../DetailComponents/AssignedUsers';
import QuestionsDetail from '../../DetailComponents/QuestionsDetail';
import { getTask } from '../../../../requests/api/task';
import { getSurveysByType } from '../../../../requests/api/survey';
import { getQuestionGuidelinesList } from '../../../../requests/api/question-guideline';
import { IMAGE, MULTIPLE_IMAGE_QUESTION } from '../../../../utils/const/questions';

function TaskDetail() {
  const taskURLPath = '/tasks';
  const styles = useStyles();
  const { t } = useTranslation();
  const { taskId } = useParams();
  const [title, setTitle] = useState(null);
  const [selectedProject, setSelectedProject] = useState({});
  const [company, setCompany] = useState({});
  const [currentOpen, setCurrentOpen] = useState(false);

  const [taskInfo, setTaskInfo] = useState({
    project: '',
    title: '',
    type: '',
    label: '',
    requiredGeoreference: false,
    revisionPolicy: '',
    surveyTeleaudit: 0,
    needReview: '',
    vertical: false,
    aiAnalysisRequired: false,
  });
  const [questions, setQuestions] = useState([]);
  const [executionSurveyTitle, setExecutionSurveyTitle] = useState(null);
  const [reviewSurveyTitle, setReviewSurveyTitle] = useState(null);

  useEffect(() => {
    const getTaskById = async () => {
      const { data, success } = await getTask(taskId);
      if (success) {
        const taskData = data.data.task;
        const labelKey = taskData.label
          ?.toLowerCase()
          .replace(/[_\s]+(.)?/g, (_, char) => (char ? char.toUpperCase() : '')) || 'withoutLabel';
        setCompany({ ...data.data.task.Project.Company });
        setSelectedProject({ ...data.data.task.Project });
        setTaskInfo({
          title: taskData.title,
          project: taskData.Project.id,
          type: { name: taskData.type },
          label: labelKey,
          requiredGeoreference: taskData.requiredGeoreference,
          revisionPolicy: taskData.revisionPolicy,
          surveyTeleaudit: taskData.surveyTeleaudit ? 'yes' : 'no',
          switchStock: taskData.switchStock,
          editable: taskData.editable,
          description: taskData.description,
          maxDistance: taskData.maxDistance,
          needReview: taskData.needReview,
          vertical: taskData.vertical,
          aiAnalysisRequired: taskData.aiAnalysisRequired,
        });
        setTitle(taskData.title);
        if (taskData.Questions.length > 0) {
          const sortedQuestions = taskData.Questions.sort(
            (a, b) => a.position - b.position,
          );

          const aiQuestions = sortedQuestions.filter(
            (question) => question.aiAnalysisRequired
              && [IMAGE, MULTIPLE_IMAGE_QUESTION].includes(question.type),
          );

          const guidelinesByQuestionId = {};
          if (aiQuestions.length > 0) {
            await Promise.all(aiQuestions.map(async (question) => {
              const guidelinesResponse = await getQuestionGuidelinesList({
                questionId: question.id,
              });
              const [guideline] = guidelinesResponse.success
                ? guidelinesResponse.data.data.questionGuidelinesList
                : [];
              if (guideline) {
                guidelinesByQuestionId[question.id] = guideline;
              }
            }));
          }

          const questionsWithGuidelines = sortedQuestions.map((question) => {
            const guideline = guidelinesByQuestionId[question.id];
            return guideline
              ? {
                ...question,
                guideline: guideline.guideline,
                nMinPictures: guideline.nMinPictures,
              }
              : question;
          });

          setQuestions(questionsWithGuidelines);
        }
      }
    };

    if (taskId) {
      getTaskById();
    }
  }, [taskId]);

  useEffect(() => {
    if (taskInfo.type?.name !== 'request') {
      setExecutionSurveyTitle(null);
      setReviewSurveyTitle(null);
      return undefined;
    }

    const loadSurveyTitles = async () => {
      const [executionResponse, reviewResponse] = await Promise.all([
        getSurveysByType('execution'),
        getSurveysByType('review'),
      ]);
      const executionSurvey = executionResponse.data?.data?.surveys?.find(
        (survey) => survey.id === taskInfo.executionSurveyId,
      );
      const reviewSurvey = reviewResponse.data?.data?.surveys?.find(
        (survey) => survey.id === taskInfo.reviewSurveyId,
      );
      setExecutionSurveyTitle(executionSurvey?.title || null);
      setReviewSurveyTitle(reviewSurvey?.title || null);
    };

    loadSurveyTitles();
    return undefined;
  }, [taskInfo.type, taskInfo.executionSurveyId, taskInfo.reviewSurveyId]);

  return (
    <>
      <div className={styles.content}>
        <div className={styles.headerContainer}>
          <div className={styles.header}>
            <IconButton
              id="backButton"
              aria-label="back"
              className={styles.backButton}
              component={Link}
              to={`${taskURLPath}`}
            >
              <ReplyOutlined fontSize="small" />
              <span>{t('groups.actionButtons.back')}</span>
            </IconButton>
            <Typography variant="h4" className={styles.title}>
              {title}
            </Typography>
          </div>
          <div className={styles.buttonsContainer}>
            <Button
              onClick={() => setCurrentOpen(true)}
              disabled={!taskInfo.project}
            >
              {t('task.taskEdition.duplicateTask')}
            </Button>
          </div>
        </div>
        <Divider />
        <div className={styles.surveyContent}>
          {!selectedProject && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '300px',
                width: '100%',
              }}
            >
              <Box sx={{ flexGrow: 'initial !important' }}>
                <CircularProgress sx={{ color: '#2C9DC9' }} />
                <p className={styles.loadingText}>{t('loading')}</p>
              </Box>
            </Box>
          )}
          <div className={styles.editionArea}>
            {selectedProject && (
              <TaskDetailComponent
                taskDetail={{
                  id: parseInt(taskId, 10),
                  companyName: company.name,
                  projectName: selectedProject.projectName,
                  title: taskInfo.title,
                  type: taskInfo.type.name,
                  label: t(`task.taskForm.${taskInfo.label || 'withoutLabel'}`),
                  requiredGeoreference: taskInfo.requiredGeoreference,
                  revisionPolicy: taskInfo.revisionPolicy === 'yes',
                  maxDistance: taskInfo.maxDistance,
                  needReview: taskInfo.needReview,
                  executionSurvey: executionSurveyTitle,
                  reviewSurvey: reviewSurveyTitle,
                  aiAnalysisRequired: taskInfo.aiAnalysisRequired,
                  verticalForm: taskInfo.vertical,
                  teleaudit: taskInfo.surveyTeleaudit === 'yes',
                  frequency: null,
                  measurementsInFrequency: null,
                  switchStock: taskInfo.switchStock,
                  description: taskInfo.description,
                }}
              ></TaskDetailComponent>
            )}
            <br />
            {/* TODO: implementar */}
            {selectedProject && (
              <AssignedUsers taskId={taskId}> </AssignedUsers>
            )}
            <br />
            {questions && (
              <QuestionsDetail
                taskId={taskId}
                questions={questions}
                formType={taskInfo.type}
                editable={taskInfo.editable}
                aiAnalysisRequired={taskInfo.aiAnalysisRequired}
              ></QuestionsDetail>
            )}
          </div>
        </div>
        <CreateTaskModal
          defaultOpen={currentOpen}
          setCurrentOpen={setCurrentOpen}
          duplicating={true}
          taskInfo={taskInfo}
          taskId={taskId}
        />
      </div>
    </>
  );
}

export default TaskDetail;
